import type { Movie } from '../types';

// 24 slasher B-movies — each with a 10-word answer pool and a back-of-box
// synopsis that obliquely evokes the world those words live in, without
// naming any of them outright.
//
// `id` is a stable slug used as the key when this catalog moves to KV.
// The picker cycles movie every day (24-day rotation) and walks each movie's
// word pool independently, so the same exact (movie, word) combo recurs only
// after roughly 8 months (24 × 10 = 240 days).
const MOVIES: readonly Movie[] = [
  {
    id: 'camp-crimson',
    name: 'CAMP CRIMSON',
    year: 1985,
    cause: 'Becky Hartwell as the Counselor',
    catchphrase: "Some campfires don't go out.",
    synopsis:
      "Lake Vermilion. Twelve years after the boys'-side fire, the moths still know the way to the dock. By the time the campfire dies, the planks are wet with footprints that lead away from the water.",
    win: 'Becky reaches the ranger station. She lives. Sequel imminent.',
    lose: 'Becky becomes the cold open of CAMP CRIMSON 2.',
    words: [
      'WOODS',
      'CABIN',
      'TENTS',
      'EMBER',
      'MOTHS',
      'LAKES',
      'CREEK',
      'PINES',
      'DOCKS',
      'STARS',
    ],
    palette: ['#1a3a1a', '#7a1818'],
    icon: '🪓',
  },
  {
    id: 'the-babysitter-never-left',
    name: 'THE BABYSITTER NEVER LEFT',
    year: 1982,
    cause: "Mark 'Marky' Stevens as the Babysitter",
    catchphrase: "He locked the doors. He's still inside.",
    synopsis:
      "Mark checks the deadbolts twice and reads to the twins until they're out. The basement door opens on its own. Something heavy moves on the upstairs landing. The line is dead. The attic light is on.",
    win: 'Mark gets the kids out the back door. The cops finally arrive.',
    lose: 'Mark becomes the headline of every milk carton in town.',
    words: [
      'PHONE',
      'ATTIC',
      'STAIR',
      'COUCH',
      'QUIET',
      'CLOSE',
      'LOCKS',
      'DOORS',
      'LAMPS',
      'KNIFE',
    ],
    palette: ['#0a1a3a', '#2a0a2a'],
    icon: '☎️',
  },
  {
    id: 'prom-queen-must-die',
    name: 'PROM QUEEN MUST DIE',
    year: 1984,
    cause: 'Cherry Whitaker as the Prom Queen',
    catchphrase: 'Crown her. Crown her again.',
    synopsis:
      "Maplewood High's Spring Fling is two hours away and Cherry Whitaker is a lock. The corsage on her wrist is fresh. The punch bowl smells faintly of iron. Her date hasn't blinked in twenty minutes.",
    win: 'Cherry crowns the killer with the punch bowl. Survives. Wins prom anyway.',
    lose: "Cherry's ghost will judge prom forever.",
    words: [
      'CROWN',
      'DRESS',
      'DANCE',
      'PUNCH',
      'GLOVE',
      'TIARA',
      'GOWNS',
      'HEELS',
      'BLOOM',
      'SATIN',
    ],
    palette: ['#ff2d92', '#a0741c'],
    icon: '👑',
  },
  {
    id: 'video-nasties',
    name: 'VIDEO NASTIES',
    year: 1987,
    cause: "Doug 'Dougie' Romero as the Clerk",
    catchphrase: "Some tapes don't rewind.",
    synopsis:
      'Aisle Nine Home Entertainment is the last rental store on Route 6 and Dougie is alone past eleven. A tape with no cover sits on the return cart. The label reads tomorrow’s date. The horror section is two titles longer than it was at open.',
    win: 'Dougie ejects the tape. Snaps the spool. Goes home.',
    lose: "Dougie is now Aisle 7. New release. Don't rent it.",
    words: [
      'TAPES',
      'AISLE',
      'SHELF',
      'VIDEO',
      'MOVIE',
      'GENRE',
      'RACKS',
      'REELS',
      'COVER',
      'CLERK',
    ],
    palette: ['#3a3a3a', '#00d9ff'],
    icon: '📼',
  },
  {
    id: 'night-of-the-lifeguard',
    name: 'NIGHT OF THE LIFEGUARD',
    year: 1986,
    cause: 'Tina Sands as the Lifeguard',
    catchphrase: "Pool's closed.",
    synopsis:
      'Tina blows the closing whistle at the Sun Valley public pool. Nobody leaves. The deep end glows green; the lane ropes drift on their own; the deck chairs are full of swimmers who do not get up.',
    win: 'Tina drains the pool. Whatever it was is gone. So is she. (To a better job.)',
    lose: 'Tina is the deep end now.',
    words: [
      'WATER',
      'POOLS',
      'DROWN',
      'TIDES',
      'FLOAT',
      'SHARK',
      'GUARD',
      'LANES',
      'WAVES',
      'DECKS',
    ],
    palette: ['#0d4f7a', '#9c1a1a'],
    icon: '🏊',
  },
  {
    id: 'jazzercise-of-death',
    name: 'JAZZERCISE OF DEATH',
    year: 1985,
    cause: "Patty 'Pixel' Marks as the Aerobics Star",
    catchphrase: 'Feel. The. Burn.',
    synopsis:
      "Patty runs the eight o'clock class at Tone Town and the mirrors never lie. Tonight they show one too many silhouettes on every count. Her leg warmers tighten between songs. The cassette is screaming.",
    win: 'Patty crushes the mirror. Crushes the killer. 1-2-3 and CRUNCH.',
    lose: "Patty's tape loops. Forever.",
    words: [
      'SWEAT',
      'HEART',
      'PULSE',
      'LYCRA',
      'BURNS',
      'DANCE',
      'BEATS',
      'STEPS',
      'LUNGE',
      'TIGHT',
    ],
    palette: ['#ff2d92', '#00d9ff'],
    icon: '🩰',
  },
  {
    id: 'pizza-hut-blood',
    name: 'PIZZA HUT BLOOD',
    year: 1988,
    cause: "Tony 'Tones' Caprese as the Delivery Boy",
    catchphrase: '30 minutes or your life is free.',
    synopsis:
      'Tony has thirty minutes to get a large pepperoni to 14 Sycamore Drive and every porch light on the block is dark. The box is hot through the cardboard. The GPS says the road ends in a field. The ticket has his name on it.',
    win: 'Tony drops the pizza. Reverses the van. Lives to deliver again.',
    lose: 'The pizza was free. So was Tony.',
    words: [
      'PIZZA',
      'CRUST',
      'SLICE',
      'DOUGH',
      'OVENS',
      'ORDER',
      'SAUCE',
      'BOXES',
      'ROADS',
      'PORCH',
    ],
    palette: ['#cc0000', '#ffd700'],
    icon: '🍕',
  },
  {
    id: 'the-paperboy',
    name: 'THE PAPERBOY',
    year: 1983,
    cause: 'Bobby Halloran as the Paperboy',
    catchphrase: 'Special delivery. Especially you.',
    synopsis:
      "Bobby works the five a.m. Halloran Herald route on his Schwinn and has never missed a porch. This morning every stoop has yesterday's paper still curled on it. His chain rattles in time with footsteps a block back.",
    win: "Bobby finishes the route. Pedals home. Doesn't tell his mom.",
    lose: "Bobby's bike is found. Bobby is not.",
    words: [
      'PAPER',
      'ROUTE',
      'BIKES',
      'EARLY',
      'ALARM',
      'NEWSY',
      'PORCH',
      'CHAIN',
      'PEDAL',
      'WAGON',
    ],
    palette: ['#ff7a2b', '#1a1a2a'],
    icon: '📰',
  },
  {
    id: 'mall-horror',
    name: 'MALL HORROR',
    year: 1986,
    cause: "Brittany 'Brit' Dale as the Window Shopper",
    catchphrase: "Open 'til midnight. Then forever.",
    synopsis:
      "Westridge Mall closes at nine and Brittany clocked out late from Spencer's. The food court fountain is on. The food court is empty. The security gates close themselves, one storefront at a time, behind her.",
    win: 'Brit makes it to the food court. The fountain is on. Dawn breaks.',
    lose: "Brit is now Bed Bath & Beyond's holiday display.",
    words: [
      'MALLS',
      'CARTS',
      'COURT',
      'STORE',
      'SHOPS',
      'SALES',
      'GATES',
      'KIOSK',
      'TILES',
      'NEONS',
    ],
    palette: ['#7c2dff', '#ff2d92'],
    icon: '🛍️',
  },
  {
    id: 'drive-in-of-the-damned',
    name: 'DRIVE-IN OF THE DAMNED',
    year: 1984,
    cause: 'Skip Hollister as the Projectionist',
    catchphrase: "Show's over.",
    synopsis:
      "The Twilight Drive-In on Route 12 hasn't closed in twenty-three years and Skip threads tonight's reel by hand. The picture is already playing. The audience is in their cars. None of them are moving.",
    win: "Skip cuts the reel. The lights come up. There's no one in the lot.",
    lose: "Skip is in the next reel. Don't watch.",
    words: [
      'REELS',
      'FILMS',
      'MOVIE',
      'MOTOR',
      'RADIO',
      'SPOOL',
      'FRAME',
      'BOOTH',
      'FOCUS',
      'SCARE',
    ],
    palette: ['#2a0a4a', '#ffd700'],
    icon: '🎬',
  },
  {
    id: 'night-of-the-goose',
    name: 'NIGHT OF THE GOOSE',
    year: 1981,
    cause: "Walter 'Wally' Honkler as the Farmhand",
    catchphrase: 'Honk. Honk.',
    synopsis:
      "Honkler Farms loses a goose to coyote most summers. Wally hasn't lost one since '78. Tonight the pond is empty and the barn rafters are full of something pacing on webbed feet. A single feather drifts past the porch light.",
    win: 'Wally outruns the goose. Lives to farm again. Buys a guard goose. Of his own.',
    lose: 'Wally is found in the pond. The goose is missing. Sheriff has no leads.',
    words: [
      'HONKS',
      'GEESE',
      'PONDS',
      'BARNS',
      'FENCE',
      'FARMS',
      'NESTS',
      'STRAW',
      'MUDDY',
      'WINGS',
    ],
    palette: ['#7a1818', '#9aa098'],
    icon: '🪿',
  },
  {
    id: 'cabin-but-worse',
    name: 'CABIN, BUT WORSE',
    year: 1986,
    cause: 'Greg & Linda Mason as the Vacationing Couple',
    catchphrase: "It's not the cabin.",
    synopsis:
      "Greg and Linda Mason rented Cabin 12 at Pinegrove Hollow for their tenth anniversary. The hearth won't stay lit. The porch boards creak in shifts. The trees they marked yesterday are closer to the door tonight.",
    win: "Greg and Linda burn the cabin to the ground. They drive away. Don't look back.",
    lose: 'The trees move on. Slowly. They have time.',
    words: [
      'TREES',
      'WOODS',
      'CABIN',
      'MOSSY',
      'FIRES',
      'CHOPS',
      'CREAK',
      'ROOTS',
      'STUMP',
      'BARKS',
    ],
    palette: ['#1a3a1a', '#cc6600'],
    icon: '🌲',
  },
  {
    id: 'hacksaw-summer',
    name: 'HACKSAW SUMMER',
    year: 1986,
    cause: "Junior 'JJ' Boggs as the Field Hand",
    catchphrase: 'Pull the cord. Run.',
    synopsis:
      'Junior Boggs cuts the back forty at Boggs Farm alone every August. Tonight the crows lift off in one breath. The wind moves the wrong way through the rows. Somewhere three rows over, an engine catches and idles.',
    win: 'JJ makes it to the silo. Climbs. Lives.',
    lose: 'JJ becomes part of the harvest.',
    words: [
      'FIELD',
      'BARNS',
      'RUSTY',
      'BLOOD',
      'SHEDS',
      'HUSKS',
      'STALK',
      'CROWS',
      'GRAIN',
      'TINES',
    ],
    palette: ['#d4a574', '#7a1818'],
    icon: '🪚',
  },
  {
    id: 'the-sleepover',
    name: 'THE SLEEPOVER',
    year: 1985,
    cause: 'Jenny, Cass & Margo as the Sleepover',
    catchphrase: 'Lights out. Forever.',
    synopsis:
      "Jenny's basement, Friday night, three sleeping bags and a stack of Care Bears VHS. The girls finish the movie. The credits scroll names no one credits. Margo doesn't come back from the kitchen.",
    win: "The girls bolt. Mom's car. Dawn. Dunkin'.",
    lose: 'Three new sleeping bags appear in the linen closet.',
    words: [
      'SLEEP',
      'DARES',
      'GHOST',
      'BLANK',
      'MOVIE',
      'PIZZA',
      'SODAS',
      'TRUTH',
      'DREAM',
      'LAMPS',
    ],
    palette: ['#ff8ec1', '#7c2dff'],
    icon: '🛏️',
  },
  {
    id: 'cheerleader-of-the-dead',
    name: 'CHEERLEADER OF THE DEAD',
    year: 1987,
    cause: 'Stacy Burke as the Squad Captain',
    catchphrase: 'Give me a K. Give me an I.',
    synopsis:
      "Stacy captains varsity at Riverside East and the away bus leaves in an hour. The locker room is too quiet for twelve. A single pompom is on the floor and it's the wrong color. The mascot's head won't come off the kid wearing it.",
    win: 'Stacy nails the routine. The killer is in the splits. Forever.',
    lose: 'Stacy is now the school spirit. Literally.',
    words: [
      'CHEER',
      'SQUAD',
      'TEAMS',
      'POMPS',
      'SPELL',
      'PEPPY',
      'SKIRT',
      'CHANT',
      'RALLY',
      'GLEAM',
    ],
    palette: ['#0a1a3a', '#cc0000'],
    icon: '📣',
  },
  {
    id: 'drive-thru-massacre',
    name: 'DRIVE-THRU MASSACRE',
    year: 1989,
    cause: 'Donna Park as the Window Girl',
    catchphrase: 'Would you like fries with that.',
    synopsis:
      'Burger Beat on Route 6 stays open till two and Donna has been at the headset alone since Kenny clocked out. The same order keeps coming through. Her order. Extra mayo. No straw. The grill is on.',
    win: 'Donna locks the window. Calls the cops. Quits via voicemail.',
    lose: 'Donna is the Wednesday special.',
    words: [
      'FRIES',
      'MEALS',
      'ORDER',
      'DRINK',
      'MENUS',
      'EATEN',
      'GRILL',
      'SHAKE',
      'STRAW',
      'TRAYS',
    ],
    palette: ['#ffcc00', '#cc0000'],
    icon: '🍔',
  },
  {
    id: 'the-new-neighbor',
    name: 'THE NEW NEIGHBOR',
    year: 1984,
    cause: 'Frank Petrowski as the Suspicious Husband',
    catchphrase: "He hasn't waved back. Not once.",
    synopsis:
      "The Petrowskis have been on Birch Lane since the cul-de-sac was poured. The new neighbor at 14 hasn't waved back, hasn't mowed, hasn't taken the trash to the curb on a Wednesday since spring. The dog won't cross the property line.",
    win: 'Frank calls the cops. Cops come. Frank is right. Block party.',
    lose: "Frank is in the new neighbor's freezer. Block party.",
    words: [
      'LAWNS',
      'FENCE',
      'PORCH',
      'HEDGE',
      'BLOCK',
      'WAVES',
      'SHEDS',
      'TRASH',
      'BLIND',
      'DEEDS',
    ],
    palette: ['#5a8a4a', '#f5e8c0'],
    icon: '🏘️',
  },
  {
    id: 'motel-check-out',
    name: 'MOTEL CHECK-OUT',
    year: 1983,
    cause: 'Sandy Vex as the Front Desk Girl',
    catchphrase: 'Vacancy.',
    synopsis:
      'The Vacancy Motor Lodge sits at the end of Route 9 and Sandy works front desk Wednesday through Sunday. Room 12 keeps ringing the bell. Room 12 hasn’t been let since the renovation. The diner across the lot is lit with no cars.',
    win: 'Sandy lights the No Vacancy sign. The hallway empties.',
    lose: 'Sandy is now permanent staff. Forever shift.',
    words: [
      'MOTEL',
      'ROOMS',
      'TOWEL',
      'GUEST',
      'NIGHT',
      'LOBBY',
      'POOLS',
      'DINER',
      'BELLS',
      'SHEET',
    ],
    palette: ['#ff7a2b', '#1a0a2a'],
    icon: '🛎️',
  },
  {
    id: 'summer-job',
    name: 'SUMMER JOB',
    year: 1986,
    cause: 'Brad Helms as the Stock Boy',
    catchphrase: 'Cleanup on aisle never.',
    synopsis:
      "Brad's last summer at Foodway Plus before college and Phil swore the overnights were dead easy. Nobody is in the aisles. Nobody is at the registers. Someone has been opening cases of frozen peas from the inside.",
    win: 'Brad clocks out. Quits. Discovers the calling of accounting.',
    lose: 'Brad is the Employee of the Month. Forever.',
    words: [
      'SHIFT',
      'STORE',
      'BREAK',
      'BORED',
      'AISLE',
      'CLOCK',
      'STOCK',
      'CRATE',
      'SHELF',
      'BADGE',
    ],
    palette: ['#0d6cb0', '#ffcc00'],
    icon: '🛒',
  },
  {
    id: 'halloween-85',
    name: "HALLOWEEN '85",
    year: 1985,
    cause: 'Lisa Crane as the Trick-or-Treater',
    catchphrase: 'Trick.',
    synopsis:
      "Lisa is eleven and her older brother dared her to ring the bell at the house on the corner. The pumpkin candle is still lit. The porch light isn't on. The kids on the sidewalk this year have masks she doesn't recognize and none of them are laughing.",
    win: "Lisa runs home. Mom's there. Dad's there. The street is empty.",
    lose: 'Lisa joins the parade. Forever.',
    words: [
      'TRICK',
      'TREAT',
      'CANDY',
      'MASKS',
      'GHOST',
      'HAUNT',
      'GHOUL',
      'GOURD',
      'SPOOK',
      'PORCH',
    ],
    palette: ['#ff7a2b', '#0a0420'],
    icon: '🎃',
  },
  {
    id: 'carwash-of-blood',
    name: 'CARWASH OF BLOOD',
    year: 1988,
    cause: "Hector 'H' Pena as the Detailer",
    catchphrase: 'Soft cloth. Hard ending.',
    synopsis:
      'Hector runs the late shift at Sudsy King and the cars in the bay tonight are running themselves through. The brushes start half a second before he hits the button. The rinse comes out warm. The wax bay smells like iron.',
    win: 'Hector kills the power. Finds nothing. Tells nobody. Quits.',
    lose: 'Hector is now Wash #4: Premium with Wax.',
    words: [
      'RINSE',
      'FOAMS',
      'WAXED',
      'BRUSH',
      'SOAPS',
      'TIRES',
      'HOSES',
      'DRYER',
      'WIPES',
      'BUFFS',
    ],
    palette: ['#888888', '#9c1a1a'],
    icon: '🚗',
  },
  {
    id: 'the-mother-in-law',
    name: 'THE MOTHER-IN-LAW',
    year: 1981,
    cause: 'Greta as Herself',
    catchphrase: 'Have you put on weight, dear.',
    synopsis:
      'Greta arrives a week early, two matching cases and a binder of opinions. She rearranges the linen closet, the spice rack, the seating chart, then the deed. The pot roast in the oven is screaming softly. The china set isn’t yours.',
    win: "Greta's flight is rescheduled. By divine intervention.",
    lose: 'Greta has the master suite. The basement now belongs to you.',
    words: [
      'ROAST',
      'TABLE',
      'STOVE',
      'SHOES',
      'PEARL',
      'LINEN',
      'CHINA',
      'DECOR',
      'CLEAN',
      'SUITS',
    ],
    palette: ['#aa3366', '#6a7a3a'],
    icon: '🍽️',
  },
  {
    id: 'pool-party-killer',
    name: 'POOL PARTY KILLER',
    year: 1984,
    cause: "Kevin & Krew as the Class of '84",
    catchphrase: "Last one in's a dead man.",
    synopsis:
      "Kevin's parents are in Sarasota and the backyard is full of the Class of '84 with a keg, a Trans Am, and a floaty shaped like Reagan. Someone is swimming laps underwater without coming up. The grill is lit but no one is eating.",
    win: 'Kevin drains the pool. Krew rolls home. Memorable summer.',
    lose: 'Kevin is the centerpiece of the floatie raft.',
    words: [
      'WATER',
      'PARTY',
      'FLOAT',
      'POOLS',
      'GLASS',
      'GUEST',
      'BEERS',
      'CHIPS',
      'GRILL',
      'TOWEL',
    ],
    palette: ['#00d9ff', '#ff7a2b'],
    icon: '🍹',
  },
  {
    id: 'the-costume-contest',
    name: 'THE COSTUME CONTEST',
    year: 1986,
    cause: 'Marsha Quinn as the Best Dressed',
    catchphrase: 'First place.',
    synopsis:
      "The Lakeside Halloween Costume Contest is in its fifteenth year and Marsha Quinn has won three running. Tonight the judges aren't blinking. Contestant 7 has fangs that don't come off. Contestant 11's robes drag and leave a smear.",
    win: 'Marsha wins. The trophy is real. The people in masks are not.',
    lose: 'Marsha gets second place. Then third. Then last. Forever.',
    words: [
      'MASKS',
      'CAPES',
      'BOOTS',
      'FANGS',
      'PROPS',
      'SCARY',
      'ROBES',
      'STAGE',
      'CROWN',
      'GLITZ',
    ],
    palette: ['#160a1c', '#ff2d92'],
    icon: '🎭',
  },
];

// Async on day one so the call site is the same when the catalog moves to KV.
// Future impl: `fetch('/api/catalog')` backed by KV, validated against the
// Movie type at the boundary.
export async function loadCatalog(): Promise<readonly Movie[]> {
  return MOVIES;
}
